import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type * as React from 'react'
import { cn } from '@/lib/utils'
import { Spinner } from './spinner'

export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap font-medium',
    'rounded-lg text-sm transition-[background-color,box-shadow,color,transform,opacity] duration-150 ease-smooth',
    'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-primary text-primary-foreground shadow-elevation-1',
          'bg-[linear-gradient(180deg,oklch(1_0_0/14%),transparent)]',
          'shadow-[inset_0_1px_0_oklch(1_0_0/18%),0_1px_2px_oklch(0_0_0/20%)] hover:bg-primary/90',
        ],
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
        outline:
          'border border-border bg-card text-foreground shadow-elevation-1 hover:bg-accent dark:bg-transparent',
        ghost: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        soft: 'bg-primary-soft text-primary-soft-foreground hover:bg-primary-soft/70',
        destructive:
          'bg-destructive text-destructive-foreground shadow-[inset_0_1px_0_oklch(1_0_0/15%)] hover:bg-destructive/90',
        'destructive-soft':
          'bg-destructive-soft text-destructive-soft-foreground hover:bg-destructive-soft/70',
        link: 'h-auto px-0 text-primary underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        xs: 'h-7 gap-1.5 rounded-md px-2.5 text-xs',
        sm: 'h-8 rounded-md px-3 text-[13px]',
        md: 'h-9 px-4',
        lg: 'h-11 rounded-xl px-5 text-[15px]',
        icon: 'size-9',
        'icon-sm': 'size-8 rounded-md',
        'icon-xs': 'size-7 rounded-md',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  },
)

export interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

export function Button({
  className,
  variant,
  size,
  asChild,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button'
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Spinner className="absolute" />}
          <span
            className={cn('inline-flex items-center justify-center [gap:inherit]', loading && 'invisible')}
          >
            {children}
          </span>
        </>
      )}
    </Comp>
  )
}
